/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ConfirmModalComponent} from './shared/confirm-modal/deprecated-modals/confirm-modal.component';
import {AlertNotificationService} from './alert-notification.service';
import {ConfirmModal} from './shared/confirm-modal/deprecated-modals/confirm-modal.model';
import {AlertNotification} from './alert.notification.model';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {lastRouteChildData} from './shared/utils/activated-router-helper/activated-route-helper';
import {AuthenticationService} from './shared/authentication/authentication.service';
import {AppService} from './app.service';
import {UserService} from './shared/services/user.service';
import {distinctUntilChanged, filter} from 'rxjs/operators';
import {SpinnerService} from './shared/spinner/spinner.service';
import {SpinnerContextTypeEnum} from './shared/spinner/spinner-context-type.enum';
import {ReplayableRouterEventsService} from './shared/services/replayable-router-events.service';
import {
    DependencyLoadNotifierService
} from './shared/services/dependency-load-notifier/dependency-load-notifier.service';
import {CarouselSeverityTypeEnum} from './shared/carousel/configuration/carousel-severity-type.enum';
import {CarouselDataModel} from './shared/carousel/models/carousel-data.model';
import {SettingApi} from './api/ms-settings/setting.api';
import {fastDeepCopy} from './shared/utils/deep-copy';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
    @ViewChild('routerOutlet', {static: true}) routerOutlet: ElementRef;
    public title = 'ORCAN';
    public showStickyHeader: boolean;
    public showSideBarMenu: boolean;
    public hasSendOption: boolean;
    public showBreadcrumbs = true;
    public isLoading = true;
    public spinnerObservable = this.spinnerService.onGlobalChange();
    showCarousel = false;
    carouselSeverityTypeInfo = CarouselSeverityTypeEnum.INFO;
    carouselData: CarouselDataModel[];
    public hasBackgroundBlur: boolean;
    private isUserFirstAccess = true;
    private lastModal: ConfirmModalComponent = null;

    private subscriptions = new Subscription();

    constructor(private modalService: NgbModal,
                private alertNotificationService: AlertNotificationService,
                private router: Router,
                private activatedRoute: ActivatedRoute,
                private authentication: AuthenticationService,
                private spinnerService: SpinnerService,
                private replayableRouter: ReplayableRouterEventsService,
                private appService: AppService,
                private userService: UserService,
                private dependencyLoadNotifier: DependencyLoadNotifierService,
                private settingApi: SettingApi) {
        this.subscriptions.add(this.spinnerObservable.subscribe((isLoading) => {
            this.isLoading = isLoading;
        }));

        this.subscriptions.add(this.dependencyLoadNotifier.onReady.subscribe(() => {
            this.isUserFirstAccess = this.userService.isFirstAccess;
            if (!this.isUserFirstAccess) {
                this.getAllCurrentAlerts();
            }
        }));

        this.spinnerService.start(SpinnerContextTypeEnum.GLOBAL, null);

        this.subscriptions.add(this.appService.onShowBackgroundBlur.pipe(distinctUntilChanged())
            .subscribe((value) => {
                this.hasBackgroundBlur = value;
            }));

        this.subscriptions.add(this.appService.onAlertConfigUpdateData
            .subscribe((value) => {
                if (value) {
                    this.getAllCurrentAlerts();
                }
            }));
    }

    @HostListener('document:click', ['$event'])
    documentClick = (event: Event): void => {
        this.appService.emitDocumentClick(event);
    };

    @HostListener('window:resize', ['$event'])
    windowResize = (event: Event): void => {
        this.appService.emitWindowResize(event);
    };

    ngOnInit(): void {
        this.authentication.initAuthentication();
        this.globalErrorModal();
        this.toggleStickyHeader();

        // TypeScript 3.8.3 doesn't have typings for Resize Observer API, so we get it from the Window object as an unknown type.
        // Resize Observer API: https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver
        const resizeObserver = new (window as any).ResizeObserver((entries: any[]) => {
            // This isn't entirely correct since entries aren't events, but they all have a target, same as Events.
            // The ResizeObserver.observe method will only match one element, hence the entries[0].
            this.appService.emitWindowResize(entries[0]);
        });
        resizeObserver.observe(this.routerOutlet.nativeElement);
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    globalErrorModal(): void {
        const subscription = this.alertNotificationService.notificationEventEmitter
            .subscribe((notification: AlertNotification) => {
                if (this.lastModal) {
                    this.lastModal.activeModal.dismiss();
                }
                this.lastModal = this.modalService.open(ConfirmModalComponent, {
                    centered: true
                }).componentInstance.initModal({
                    titleText: notification.alertNotificationSeverity,
                    subtitleText: notification.message,
                    details: notification.details,
                    alertNotificationSeverity: notification.alertNotificationSeverity,
                    primaryButtonText: null,
                    secondaryButtonText: 'Close'
                } as ConfirmModal);
            });
        this.subscriptions.add(subscription);
    }

    toggleStickyHeader(): void {
        const subscription = this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe(() => {
                this.showSideBarMenu = !!lastRouteChildData(this.activatedRoute)['hasSideBarMenu'] && !this.isUserFirstAccess;
                this.showBreadcrumbs = !lastRouteChildData(this.activatedRoute)['hideBreadcrumbs'];
                this.showStickyHeader = !!lastRouteChildData(this.activatedRoute)['hasStickyHeader'] && !this.isUserFirstAccess;
            });
        this.subscriptions.add(subscription);
    }

    onRouterOutletContainerScroll(event: Event): void {
        this.appService.emitRouterOutletContainerScroll(event);
    }

    private getAllCurrentAlerts(): void {
        this.carouselData = [];
        this.subscriptions.add(
            this.settingApi.getAllCurrentAlerts()
                .subscribe((alerts) => {
                    if (alerts.length > 0) {
                        alerts.forEach((alert) => {
                            this.carouselData.push({
                                title: alert.name,
                                description: alert.description
                            });
                        });
                        this.showCarousel = true;
                    }
                    this.carouselData = fastDeepCopy(this.carouselData);
                }));
    }
}
